import {
    ActionType,
    displayNameById,
    File,
    FileEntityId,
    FileId,
    FileManagementLevel,
    formatDate,
    formatNumber,
    genInternalId,
    globalFilesEntityId,
    IFile,
    isFilePreviewable,
    isPdf,
} from "@sp-crm/core";
import { AccessControlEditor } from "components/access-control/access-control-editor";
import { DocLink } from "components/app/doc-link";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { DeleteButton } from "components/ui/action-button";
import { Checkbox } from "components/ui/checkbox";
import { FileUpload, UploadedFile } from "components/ui/file-upload";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryButton } from "components/ui/primary-button";
import {
    RegionVisibility,
    RegionVisibilityTree,
} from "components/ui/region-visibility-tree";
import { SearchInput } from "components/ui/search-input";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import { Formik, FormikErrors } from "formik";
import {
    FileEntityType,
    GetFileQuery,
    useDeleteFileMutation,
    useGetFileQuery,
    useGetSignatureRequestsForEntityQuery,
    useUpdateFileMutation,
    WhiteLabelContentMode,
} from "generated/graphql";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useWhiteLabelContentMode } from "store/selectors/branding";
import { useIsAllowed, useRegionId } from "store/selectors/hooks";
import { filesDashboardSlice } from "store/slices/files-dashboard";
import { ApplicationState } from "store/state";
import { pluralize } from "util/pluralize";
import { truncate } from "util/text";
import { LoadingState } from "../../constants/loading";
import * as actions from "../../store/actions/files";
import { FileIcon, SignableDocumentIcon } from "./file-icon";
import { FilePreview } from "./file-preview";
import { SignatureRequestList } from "./signature-request/signature-request-list";
import { SignatureRequestPanel } from "./signature-request/signature-request-panel";

interface FilesControlProps {
    entityId: FileEntityId;
    entityType?: FileEntityType;
    includeSignwise?: boolean;
    showSearch?: boolean;
}

interface DisplayableFile {
    fileId: FileId;
    createdAt: Date;
    description: string;
    originalFilename: string;
    sortableFilename: string;
    sortableUsername: string;
    displayUsername: string;
    mimeType: string;
    hasSignatureTemplate: boolean;
}

type FileAccessControl = GetFileQuery["getFile"]["accessControls"][0];

const initialSort = [
    {
        id: "sortableFilename",
        desc: false,
    },
];

export const FilesControl: React.FC<FilesControlProps> = props => {
    const { entityId, entityType, includeSignwise, showSearch } = props;
    const signwiseEnabled = includeSignwise !== false;
    const { updateQuery } = filesDashboardSlice.actions;
    const files = useSelector((state: ApplicationState) => state.files.files[entityId]);
    const fileSearch = useSelector(
        (state: ApplicationState) => state.filesDashboard[entityId] || { query: "" },
    );
    const loadingState = useSelector(
        (state: ApplicationState) =>
            state.files.loading[entityId] || LoadingState.NOTSTARTED,
    );
    const users = useSelector((state: ApplicationState) => state.users.users);
    const dispatch = useDispatch();
    const regionId = useRegionId();
    const [selectedFile, setSelectedFile] = React.useState<DisplayableFile | null>(null);
    const [showSignWisePanel, setShowSignWisePanel] = React.useState(false);
    const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);
    const prevUploadingRef = React.useRef(false);

    React.useEffect(() => {
        const isUploading = uploadedFiles.some(f => f.status === "uploading");
        const hasCompleted = uploadedFiles.some(f => f.status === "completed");
        if (prevUploadingRef.current && !isUploading && hasCompleted) {
            actions.loadFilesAction(entityId, dispatch);
        }
        prevUploadingRef.current = isUploading;
    }, [uploadedFiles, entityId, dispatch]);
    React.useEffect(() => {
        if (loadingState !== LoadingState.DONE) {
            actions.loadFilesAction(entityId, dispatch);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const closePanel = React.useCallback(() => {
        setSelectedFile(null);
    }, []);

    const handleNewSignWise = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            setShowSignWisePanel(true);
        },
        [setShowSignWisePanel],
    );

    const handleCloseSignWise = React.useCallback(() => {
        setShowSignWisePanel(false);
    }, [setShowSignWisePanel]);

    const onUpdateQuery = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(updateQuery([entityId, e.target.value])),
        [dispatch, updateQuery, entityId],
    );

    const signWiseRequestsQuery = useGetSignatureRequestsForEntityQuery(
        { entityId },
        { keepPreviousData: true },
    );

    const columns: CrmTableProps<DisplayableFile>["columns"] = React.useMemo(
        () => [
            {
                key: "sortableFilename",
                header: "Name",
                renderCell: ({ record }) => (
                    <a
                        href="#"
                        onClick={(
                            e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
                        ) => {
                            e.preventDefault();
                            setSelectedFile(record);
                        }}
                        rel="noreferrer"
                        className={`${defaultLinkStyle} flex items-center space-x-2`}>
                        {record.hasSignatureTemplate ? (
                            <SignableDocumentIcon />
                        ) : (
                            <FileIcon mimeType={record.mimeType} />
                        )}
                        <div>{record.originalFilename ?? "(untitled)"}</div>
                    </a>
                ),
            },
            {
                key: "description",
                header: "Description",
            },
            {
                key: "createdAt",
                header: "Uploaded",
                renderCell: ({ record }) => <span>{formatDate(record.createdAt)}</span>,
            },
            {
                key: "sortableUsername",
                header: "Uploaded by",
                renderCell: ({ record }) => <span>{record.displayUsername}</span>,
            },
        ],
        [setSelectedFile],
    );

    const displayableFiles = React.useMemo(() => {
        return (files || [])
            .filter(f => !f.deleted)
            .filter(
                f =>
                    fileSearch.query.length === 0 ||
                    (f.originalFilename.toLocaleLowerCase() ?? "").includes(
                        (fileSearch.query ?? "").toLocaleLowerCase(),
                    ),
            )
            .map(f => ({
                fileId: f.id,
                createdAt: f.createdAt,
                originalFilename: f.originalFilename,
                sortableFilename: f.originalFilename.toLocaleLowerCase(),
                description: truncate(f.description, 60),
                placeholder: {},
                mimeType: f.mimeType,
                sortableUsername: displayNameById(f.createdBy, users).toLocaleLowerCase(),
                displayUsername: displayNameById(f.createdBy, users),
                hasSignatureTemplate: f.hasSignatureTemplate,
            }));
    }, [files, users, fileSearch.query]);

    if (
        loadingState === LoadingState.NOTSTARTED ||
        loadingState === LoadingState.LOADING
    ) {
        return <Spinner />;
    }
    if (loadingState === LoadingState.ERROR) {
        return (
            <InlineBanner type="error">
                Sorry, there was an error loading the files. Please reload.
            </InlineBanner>
        );
    }
    if (loadingState === LoadingState.DONE) {
        return (
            <div className="space-y-4">
                <FileUpload
                    uploadEndpoint={`/api/files/${entityId}`}
                    additionalFormData={regionId ? { regionId } : undefined}
                    maxSizePerFile={50 * 1024 * 1024}
                    maxTotalSize={500 * 1024 * 1024}
                    maxFiles={50}
                    onFilesChange={setUploadedFiles}
                />
                {showSearch ? (
                    <div className="flex space-x-2 items-center justify-between">
                        <div className="flex-1 max-w-lg">
                            <SearchInput
                                label="Search"
                                type="text"
                                onChange={onUpdateQuery}
                                value={fileSearch.query ?? ""}
                                placeholder="File name"
                            />
                        </div>
                    </div>
                ) : null}
                {signwiseEnabled ? (
                    <div>
                        <SecondaryButton onClick={handleNewSignWise}>
                            New SignWise request
                        </SecondaryButton>
                    </div>
                ) : null}
                {signWiseRequestsQuery.data?.getSignatureRequestsForEntity?.length > 0 ? (
                    <div>
                        <p className="text-lg">SignWise requests</p>
                        <div className="bg-white rounded border border-gray-200 mt-2">
                            <SignatureRequestList
                                refetchSignatures={signWiseRequestsQuery.refetch}
                                signatureRequests={
                                    signWiseRequestsQuery.data
                                        .getSignatureRequestsForEntity
                                }
                            />
                        </div>
                    </div>
                ) : null}
                <div>
                    <p className="text-lg">All files</p>
                    {displayableFiles.length === 0 && !fileSearch.query ? (
                        <div className="space-y-1 mt-4">
                            <div className="text-gray-500 flex items-center space-x-1">
                                <FileIcon mimeType="pdf" />
                                <div>No files uploaded yet.</div>
                            </div>
                            <div className="text-sm text-gray-300">
                                Drag and drop files above to get started.
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded border border-gray-200 mt-2">
                            <CrmTable
                                initialSort={initialSort}
                                columns={columns}
                                data={displayableFiles}
                            />
                        </div>
                    )}
                </div>
                <ResponsivePanel
                    onDismiss={closePanel}
                    type={
                        isFilePreviewable(selectedFile)
                            ? PanelType.extraLarge
                            : PanelType.large
                    }
                    isOpen={!!selectedFile}
                    headerText="">
                    {selectedFile ? (
                        <FileEdit
                            isGlobalFile={entityId === globalFilesEntityId}
                            close={closePanel}
                            fileId={selectedFile.fileId}
                        />
                    ) : null}
                </ResponsivePanel>
                <SignatureRequestPanel
                    entityId={entityId}
                    fileEntityType={entityType}
                    isOpen={showSignWisePanel}
                    onDismiss={handleCloseSignWise}
                />
            </div>
        );
    }
    throw "Unknown rendering state.";
};

interface FileEditProps {
    fileId: FileId;
    close: () => void;
    isGlobalFile: boolean;
}

interface FileEditFormValues {
    description: string;
    originalFilename: string;
    shareWithClient: boolean;
    shareWithCommunity: boolean;
    shareWithReferral: boolean;
    accessControls: FileAccessControl[];
    regionVisibility: RegionVisibility[];
}

export const FileEdit: React.FC<FileEditProps> = props => {
    const { fileId, close, isGlobalFile } = props;
    const dispatch = useDispatch();
    const updateMutation = useUpdateFileMutation();
    const deleteMutation = useDeleteFileMutation();
    const fileQuery = useGetFileQuery({ fileId });
    const [deleteError, setDeleteError] = React.useState<string | null>(null);
    const [showSignatureTemplateWarning, setShowSignatureTemplateWarning] =
        React.useState(false);
    const onDelete = React.useCallback(async () => {
        try {
            setDeleteError(null);
            await deleteMutation.mutateAsync({ fileId });
            const entityId: FileEntityId = fileQuery.data?.getFile?.entityId ?? null;
            dispatch(actions.deleteFilePayload(fileId, entityId));
            close();
        } catch (e) {
            setDeleteError(`Could not delete this file: ${e.message}`);
        }
    }, [dispatch, deleteMutation, fileQuery, close, fileId]);
    const canModifyAcls = useIsAllowed(ActionType.ModifyAcls);
    const whiteLabelContentMode = useWhiteLabelContentMode();

    const onSubmit = React.useCallback(
        async (values: FileEditFormValues) => {
            const result = await updateMutation.mutateAsync({
                fileId,
                details: {
                    description: values.description,
                    originalFilename: values.originalFilename,
                    shareWithClient: values.shareWithClient,
                    shareWithCommunity: values.shareWithCommunity,
                    shareWithReferral: values.shareWithReferral,
                },
                accessControls: canModifyAcls ? values.accessControls : null,
                regionVisibility:
                    values.regionVisibility.length > 0
                        ? { regions: values.regionVisibility }
                        : null,
            });
            fileQuery.refetch();
            dispatch(
                actions.updateFilePayload(
                    genInternalId(),
                    File.load(result.updateFile as unknown as IFile),
                ),
            );
            close();
        },
        [fileQuery, dispatch, updateMutation, close, fileId, canModifyAcls],
    );
    const validateFormInput = (
        values: FileEditFormValues,
    ): FormikErrors<FileEditFormValues> => {
        const errors: FormikErrors<FileEditFormValues> = {};
        if (!values.originalFilename || values.originalFilename.length === 0) {
            errors.originalFilename = "Filename is required.";
        }
        if (values.originalFilename.length > 255) {
            errors.originalFilename = "Filename must be 255 characters or less.";
        }
        return errors;
    };
    return (
        <QueryRenderer name="File edit" query={fileQuery}>
            {data => {
                const initialValues: FileEditFormValues = {
                    description: data.getFile.description,
                    originalFilename: data.getFile.originalFilename,
                    shareWithClient: data.getFile.shareWithClient,
                    shareWithCommunity: data.getFile.shareWithCommunity,
                    shareWithReferral: data.getFile.shareWithReferral,
                    accessControls: data.getFile.accessControls,
                    regionVisibility:
                        data.getFile.regionVisibility?.regions?.map(r => ({
                            regionId: r.regionId,
                            mode: r.mode,
                        })) || [],
                };
                const outstandingSignatureRequestsCount =
                    data.getFile.outstandingSignatureRequestsCount;
                const requestPluralized = pluralize(
                    outstandingSignatureRequestsCount,
                    "request",
                );
                const systemManaged =
                    data.getFile.managementLevel === FileManagementLevel.System;
                const readonly = data.getFile.regionVisibility?.editable === false;

                const isFilePdfInContext = isPdf(data.getFile.mimeType);

                if (systemManaged) {
                    return (
                        <div className="space-y-4">
                            <InlineBanner type="info">
                                This file is managed by the community.
                            </InlineBanner>
                            <div>{data.getFile.originalFilename}</div>
                            <div>
                                <a
                                    href={`/api/files/download/${fileId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`${defaultLinkStyle} flex items-center space-x-2`}>
                                    <FileIcon mimeType={data.getFile.mimeType} />
                                    <div>Download</div>
                                </a>
                            </div>
                            <FilePreview file={data.getFile} />
                        </div>
                    );
                }

                return (
                    <Formik
                        onSubmit={onSubmit}
                        validate={validateFormInput}
                        initialValues={initialValues}>
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            touched,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <div className="space-y-4">
                                    {readonly ? (
                                        <InlineBanner type="info">
                                            This file is read-only because it is managed
                                            by your administrator. Contact your
                                            administrator to make changes to this file.
                                        </InlineBanner>
                                    ) : null}
                                    <div className="flex items-center justify-between space-x-2">
                                        <a
                                            href={`/api/files/download/${fileId}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`${defaultLinkStyle} flex items-center space-x-2`}>
                                            <FileIcon mimeType={data.getFile.mimeType} />
                                            <div>{data.getFile.originalFilename}</div>
                                        </a>
                                        {readonly ? null : (
                                            <DeleteButton
                                                backgroundColor="bg-white"
                                                onClick={onDelete}
                                                confirm={
                                                    outstandingSignatureRequestsCount > 0
                                                        ? {
                                                              title: "Delete SignWise file?",
                                                              message: `${
                                                                  data.getFile
                                                                      .originalFilename
                                                              } has ${formatNumber(
                                                                  outstandingSignatureRequestsCount,
                                                              )} outstanding SignWise ${requestPluralized}. Deleting this file will cancel the ${requestPluralized}. Are you sure you want to proceed?`,
                                                          }
                                                        : {
                                                              title: "Delete file?",
                                                              message: `Really delete ${data.getFile.originalFilename}?`,
                                                          }
                                                }
                                            />
                                        )}
                                    </div>
                                    {deleteError ? (
                                        <InlineBanner type="error">
                                            {deleteError}
                                        </InlineBanner>
                                    ) : null}
                                    <div>
                                        <Input
                                            label="Name"
                                            name="originalFilename"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.originalFilename}
                                            disabled={readonly}
                                        />
                                        {touched.originalFilename &&
                                        errors.originalFilename ? (
                                            <div className="text-red-700 font-bold mt-1">
                                                {errors.originalFilename}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div>
                                        <TextArea
                                            label="Description"
                                            name="description"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            rows={6}
                                            value={values.description}
                                            disabled={readonly}
                                        />
                                    </div>

                                    <div>
                                        <p>Record visibility</p>
                                        <Checkbox
                                            label="Can be shared with clients"
                                            name="shareWithClient"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            checked={values.shareWithClient}
                                            disabled={readonly}
                                        />
                                        <Checkbox
                                            label="Can be shared with communities"
                                            name="shareWithCommunity"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            checked={values.shareWithCommunity}
                                            disabled={readonly}
                                        />
                                        <Checkbox
                                            label="Can be shared with referrals"
                                            name="shareWithReferral"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            checked={values.shareWithReferral}
                                            disabled={readonly}
                                        />
                                    </div>
                                    {readonly ? null : (
                                        <AccessControlEditor
                                            label="User visibility"
                                            accessControls={values.accessControls}
                                            onChange={ac =>
                                                setFieldValue("accessControls", ac)
                                            }
                                        />
                                    )}
                                    {readonly ? null : (
                                        <RegionVisibilityTree
                                            editable={
                                                data.getFile.regionVisibility?.editable
                                            }
                                            actionType={ActionType.CreateClient}
                                            regionVisibility={values.regionVisibility}
                                            onChange={rv =>
                                                setFieldValue("regionVisibility", rv)
                                            }
                                            entityName="file"
                                        />
                                    )}
                                    <div className="mt-1">
                                        <a
                                            href={`/api/files/download/${fileId}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`${defaultLinkStyle} flex items-center space-x-2`}>
                                            <FileIcon mimeType={data.getFile.mimeType} />
                                            <div>Download</div>
                                        </a>
                                        {isGlobalFile ? (
                                            <>
                                                <Link
                                                    className={`${defaultLinkStyle} flex items-center space-x-2 min-h-[44px] py-2`}
                                                    to={
                                                        isFilePdfInContext
                                                            ? `/files/${fileId}/template`
                                                            : "#"
                                                    }
                                                    onClick={e => {
                                                        if (!isFilePdfInContext) {
                                                            e.preventDefault();
                                                            setShowSignatureTemplateWarning(
                                                                true,
                                                            );
                                                        }
                                                    }}>
                                                    <SignableDocumentIcon />
                                                    <div>Signature template</div>
                                                </Link>
                                                {showSignatureTemplateWarning ? (
                                                    <div className="mt-2">
                                                        {whiteLabelContentMode ===
                                                        WhiteLabelContentMode.Full ? (
                                                            <InlineBanner type="info">
                                                                Signature templates are
                                                                only available for PDF
                                                                files. This file is not a
                                                                PDF. For more information,
                                                                see our guide on
                                                                converting documents to
                                                                PDFs.{" "}
                                                                <DocLink
                                                                    path="/article/173-how-to-convert-your-document-to-pdf-for-signwise"
                                                                    className={`${defaultLinkStyle} flex items-center space-x-2`}>
                                                                    <div className="underline">
                                                                        Convert Your
                                                                        Document to PDF
                                                                        for SignWise
                                                                    </div>
                                                                </DocLink>
                                                            </InlineBanner>
                                                        ) : (
                                                            <InlineBanner type="info">
                                                                Signature templates are
                                                                only available for PDF
                                                                files. Please convert this
                                                                file to a PDF and
                                                                re-upload it to create a
                                                                signature template.
                                                            </InlineBanner>
                                                        )}
                                                    </div>
                                                ) : null}
                                            </>
                                        ) : null}
                                    </div>
                                    <div className="flex justify-end space-x-4">
                                        <SecondaryButton type="button" onClick={close}>
                                            Cancel
                                        </SecondaryButton>
                                        <PrimaryButton
                                            disabled={
                                                readonly ||
                                                (isGlobalFile &&
                                                    values.regionVisibility.length === 0)
                                            }
                                            type="submit">
                                            Save
                                        </PrimaryButton>
                                    </div>
                                </div>
                                <FilePreview file={data.getFile} />
                            </form>
                        )}
                    </Formik>
                );
            }}
        </QueryRenderer>
    );
};
